import React, { useRef, useState } from "react";
import { Card, Button, Row, Col } from "react-bootstrap";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import useProjectOverviewData from "./api/useProjectOverviewData";
import ProjectOverviewV2Form from "./ProjectOverviewV2Form";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import useOauth from "@utils/customHooks/useOauth";
import API from "../../../utils/API/API";
import { formatUnprocessibleResponse } from "@utils/helperFunctions";
import {
  useRestartScopingForProjectByIdMutation,
  useCreateBlueprintFromProjectMutation,
} from "@generated";

interface Props {
  projectId: string;
  statusLabels?: { status: string; label: string }[];
  salesExecutiveCreatePermission?: boolean;
  projectOverviewPrivilege?: string;
  requestApprovalPrivilege?: string;
  blueprintCreatePermission?: boolean;
}

const ProjectOverviewV2: React.FC<Props> = ({
  projectId,
  statusLabels = [],
  salesExecutiveCreatePermission = false,
  projectOverviewPrivilege,
  requestApprovalPrivilege,
  blueprintCreatePermission,
}) => {
  const submitRef = useRef<any>(null);
  const [authorizationCode] = useOauth();
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;

  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">(
    "success"
  );
  const [showRescopeModal, setShowRescopeModal] = useState(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);

  const [restartScoping] = useRestartScopingForProjectByIdMutation();
  const [createBlueprintFromProject] = useCreateBlueprintFromProjectMutation();

  const {
    accountSlug,
    accountId,
    isCrm,
    isPsa,
    fieldLabels,
    projectData,
    includedClient,
    includedPaymentTerm,
    includedBusinessUnit,
    includedSalesExec,
    includedPresales,
    includedCrm,
    includedRateTable,
    includedExternalRequest,
    includedPsa,
    clients,
    clientNames,
    salesExecOptions,
    presalesOptions,
    paymentTermOptions,
    rateTableOptions,
    businessUnitOptions,
    projectLocations,
    privileges,
    isLoading,
    hasError,
    refetchProject,
  } = useProjectOverviewData(projectId);

  if (isLoading) {
    return <ScopeStackSpinner />;
  }

  const projectStatus = projectData?.attributes?.status || "";

  const canEdit =
    projectOverviewPrivilege === "manage" ||
    projectOverviewPrivilege === "purge";

  const previouslyApproved =
    projectStatus === "won" ||
    projectStatus === "lost" ||
    projectStatus === "approved";

  const externalRequestStatus =
    (includedExternalRequest?.attributes as any)?.status || null;
  const externalRequestId = includedExternalRequest?.id || null;

  const showOfferServices =
    previouslyApproved &&
    (externalRequestStatus === "accepted" ||
      externalRequestStatus === "change_request");

  const showRescope =
    projectStatus === "won" || projectStatus === "approved";

  const showCreateBlueprint =
    (projectStatus === "won" || projectStatus === "approved") &&
    blueprintCreatePermission;

  const handleRescope = () => {
    restartScoping({ slug: accountSlug, id: Number(projectId) })
      .unwrap()
      .then(() => {
        setAlertVariant("success");
        setAlertText("The Project has been resubmitted for scoping");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          window.location.reload();
        }, 2000);
      })
      .catch(() => {
        setAlertVariant("danger");
        setAlertText("Something went wrong rescoping the project.");
        setShowAlert(true);
      });
  };

  const handleCreateBlueprint = () => {
    createBlueprintFromProject({
      slug: accountSlug,
      projectId: Number(projectId),
    })
      .unwrap()
      .then((res: any) => {
        const blueprintId = res?.data?.id;
        if (blueprintId) {
          window.location.href = `/admin/blueprints/${blueprintId}/edit`;
        }
      })
      .catch(() => {
        setAlertVariant("danger");
        setAlertText("Something went wrong creating the blueprint.");
        setShowAlert(true);
      });
  };

  const handleOfferServices = () => {
    if (!externalRequestId) return;
    API.Put(
      `${apiHost}/${accountSlug}/v1/external-requests/${externalRequestId}/offer`,
      null,
      authorizationCode
    )
      .then((res: any) => {
        if (res.status === 200) {
          setAlertVariant("success");
          setAlertText(
            `The Project has been offered to ${
              (includedClient?.attributes as any)?.name || "the client"
            }`
          );
          setShowAlert(true);
          setTimeout(() => {
            setShowAlert(false);
            window.location.reload();
          }, 2000);
        }
      })
      .catch((err: any) => {
        setAlertVariant("danger");
        setAlertText(formatUnprocessibleResponse(err, "Project"));
        setShowAlert(true);
      });
  };

  return (
    <div className="projectOverviewV2">
      {showAlert && (
        <ToastAlert
          variant={alertVariant}
          onClose={() => setShowAlert(false)}
          text={alertText}
        />
      )}
      <Card>
        <Card.Header
          style={{
            backgroundColor: "#fff",
            color: "#1C2655",
            padding: "0px 16px",
          }}
        >
          <Row style={{ padding: "20px 0 0 20px", alignItems: "center" }}>
            <Col sm={1}>
              <Button
                className="btnSeawhite"
                onClick={() => (window.location.href = "/projects")}
              >
                Back
              </Button>
            </Col>
            <Col sm={7} className="text24">
              Project Overview
            </Col>
            <Col
              sm={4}
              style={{
                textAlign: "right",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              {showRescope && (
                <Button
                  className="btnSeawhite"
                  onClick={() => setShowRescopeModal(true)}
                >
                  Rescope
                </Button>
              )}
              {showCreateBlueprint && (
                <Button
                  className="btnSeawhite"
                  onClick={() => setShowBlueprintModal(true)}
                >
                  Create Blueprint
                </Button>
              )}
              {showOfferServices && (
                <Button
                  className="btnSeawhite"
                  onClick={handleOfferServices}
                >
                  Offer Services
                </Button>
              )}
              {canEdit && projectStatus === "building" && (
                <Button
                  className="squareGreenButton"
                  onClick={() => {
                    submitRef.current?.saveProject();
                  }}
                >
                  Save
                </Button>
              )}
            </Col>
          </Row>
        </Card.Header>
        <hr style={{ marginLeft: "30px", marginRight: "30px" }} />
        <Card.Body>
          <ProjectOverviewV2Form
            ref={submitRef}
            projectId={projectId}
            accountId={accountId}
            accountSlug={accountSlug}
            fieldLabels={fieldLabels}
            isCrm={isCrm}
            isPsa={isPsa}
            projectData={projectData}
            includedClient={includedClient}
            includedPaymentTerm={includedPaymentTerm}
            includedBusinessUnit={includedBusinessUnit}
            includedSalesExec={includedSalesExec}
            includedPresales={includedPresales}
            includedCrm={includedCrm}
            includedRateTable={includedRateTable}
            clients={clients}
            clientNames={clientNames}
            salesExecOptions={salesExecOptions}
            presalesOptions={presalesOptions}
            paymentTermOptions={paymentTermOptions}
            rateTableOptions={rateTableOptions}
            businessUnitOptions={businessUnitOptions}
            projectLocations={projectLocations}
            refetchProject={refetchProject}
            salesExecutiveCreatePermission={salesExecutiveCreatePermission}
          />
        </Card.Body>
      </Card>

      <ScopeStackModal
        show={showRescopeModal}
        modalTitle="Rescope Project"
        modalBody="Are you sure you want to rescope this project? It will be resubmitted for scoping."
        button1Text="Cancel"
        button2Text="Rescope"
        handleClose={() => setShowRescopeModal(false)}
        handleButton1Click={() => setShowRescopeModal(false)}
        handleButton2Click={() => {
          setShowRescopeModal(false);
          handleRescope();
        }}
      />

      <ScopeStackModal
        show={showBlueprintModal}
        modalTitle="Create Project Blueprint"
        modalBody="Are you sure you want to create a blueprint from this project?"
        button1Text="Cancel"
        button2Text="Create Blueprint"
        handleClose={() => setShowBlueprintModal(false)}
        handleButton1Click={() => setShowBlueprintModal(false)}
        handleButton2Click={() => {
          setShowBlueprintModal(false);
          handleCreateBlueprint();
        }}
      />
    </div>
  );
};

export default ProjectOverviewV2;
